import { LocalMallOutlined } from "@mui/icons-material";
import { Masonry } from "@mui/lab";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../components/Layout/PageContainer";
import NextLink from "../../components/others/NextLink";
import Seo from "../../components/others/Seo";
import { useShopContext } from "../../context/shop/shop.context";
import { ShopActionType } from "../../context/shop/shop.types";
import { getProducts } from "../../lib/woocommerce/api";
import { Product } from "../../types/woocommerce";
import { useMySnackbar } from "../../hooks/useMySnackbar";

const Shop = ({ products }: { products: Product[] }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.up("md"));
  const { shopDispatch } = useShopContext();
  const { displaySuccessSnackbar, closeSnackbar } = useMySnackbar();

  const productList = products.map((product) => {
    const featuredImage = product.images?.[0];
    return (
      <Box key={product.id}>
        <Card sx={{ maxWidth: 345, mt: { md: 0, xs: 2 } }}>
          <NextLink href={`/shop/${product.id}_${product.slug}`}>
            {featuredImage?.src && (
              <CardMedia
                component="img"
                height="200"
                style={{ objectFit: "cover", objectPosition: "top" }}
                src={featuredImage.src}
                alt={featuredImage.alt ?? ""}
              />
            )}
            <CardContent>
              <Typography gutterBottom variant="h5" component="div">
                {product.name}
              </Typography>
            </CardContent>
          </NextLink>

          <CardActions
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Button
              size="small"
              sx={{ mb: 2 }}
              variant="contained"
              onClick={() => {
                shopDispatch({
                  type: ShopActionType.ADD_ITEM,
                  payload: { item: { product, quantity: 1 } },
                });
                displaySuccessSnackbar(t("snackbar.add_item"), (id) => (
                  <NextLink href="/shop/cart">
                    <Button
                      onClick={() => closeSnackbar(id)}
                      variant="contained"
                    >
                      {t("snackbar.see_cart")}
                    </Button>
                  </NextLink>
                ));
              }}
            >
              <LocalMallOutlined sx={{ mr: 1 }} />
              <Typography variant="body2">{t("articles.add_cart")}</Typography>
            </Button>
          </CardActions>
        </Card>
      </Box>
    );
  });
  return (
    <PageContainer>
      <Seo
        title={t("page_shop.seo.title") && undefined}
        description={t("page_shop.seo.description") ?? undefined}
      />
      <Stack
        sx={{
          background: theme.palette.background.layout,
          width: "100%",
          py: 2,
        }}
      >
        <Typography variant="h3" align="center">
          {t("page_shop.title")}
        </Typography>
      </Stack>
      {/* <Typography variant="h6" align="center" py={5}>
        {t("page_shop.subtitle")}
      </Typography> */}
      <Stack
        sx={{
          background: theme.palette.background.layout,
          width: "100%",
          alignItems: "center",
          py: 2,
        }}
      >
        <Stack maxWidth="lg" width="100%">
          {matches ? (
            <Masonry
              columns={{ sm: 2, md: 3, lg: 4 }}
              spacing={{ sm: 2, md: 3, lg: 3 }}
            >
              {productList}
            </Masonry>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {productList}
            </Box>
          )}
        </Stack>
      </Stack>
    </PageContainer>
  );
};

export async function getStaticProps() {
  const products = await getProducts({ per_page: 20 });
  return {
    props: {
      products: products || [],
    },
  };
}

export default Shop;
